<template>
  <v-container fluid fill-height>
    <v-layout row wrap>
      <v-flex xs12 sm12>
        <v-card class="elevation-1">
          <v-card-title primary-title class="px-4">
            <div>
              <h3 class="display-1 font-weight-medium mb-0">{{ $t("New beneficiaries by contract") }}</h3>
            </div>
          </v-card-title>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-card-text>
              <div class="search-bar">
                <v-autocomplete
                  v-model="organization"
                  :items="items"
                  :search-input.sync="search"
                  :label="$t('Organization')"
                  persistent-hint
                  prepend-icon="mdi-city"
                  clearable
                >
                  <template slot="no-data">
                    <v-list-tile>
                      <v-list-tile-title>
                        {{ $t("Please refine your search...") }}
                      </v-list-tile-title>
                    </v-list-tile>
                  </template>

                  <template slot="selection" slot-scope="data">
                    {{ data.item }}
                  </template>

                  <template slot="item" slot-scope="data">
                    <v-list-tile-content>
                      <v-list-tile-title v-text="data.item"></v-list-tile-title>
                    </v-list-tile-content>
                  </template>
                  <template class="pb-2" v-slot:append-outer> </template>
                </v-autocomplete>
                <v-btn @click="getMembers()" :disabled="!organization" color="primary" class="white--text">
                  {{ $t("Search") }}
                  <v-icon class="px-2" medium v-text="'mdi-account-search'" color="white"></v-icon>
                </v-btn>
              </div>
            </v-card-text>

            <v-card-text v-if="members.length > 0">
              <v-data-table
                v-model="selected"
                :headers="headers"
                :items="members"
                item-key="name"
                select-all
                class="elevation-1"
              >
                <template v-slot:items="props">
                  <td>
                    <v-checkbox v-model="props.selected" primary hide-details></v-checkbox>
                  </td>
                  <td>{{ props.item.name }}</td>
                  <td>{{ props.item.email }}</td>
                  <td>{{ props.item.phone }}</td>
                  <td>{{ props.item.departement }}</td>
                  <td>{{ props.item.jobTitle }}</td>
                  <td>
                    <v-checkbox v-model="props.item.notify" primary hide-details></v-checkbox>
                  </td>
                </template>
              </v-data-table>
            </v-card-text>

            <v-card-text v-if="selected.length > 0">
              <v-layout row wrap>
                <v-flex xs3 class="pt-4">
                  <strong>{{ $t("Role") }} :</strong>
                </v-flex>
                <v-flex xs9>
                  <v-radio-group
                    v-model="beneficiaries.role"
                    row
                    mandatory
                    color="primary"
                    :rules="[() => !!beneficiaries.role || $i18n.t('Required field')]"
                    :disabled="!isAdmin"
                  >
                    <v-radio :label="$i18n.t('customer')" value="customer"></v-radio>
                    <v-radio :label="$i18n.t('operational manager')" value="operational manager"></v-radio>
                    <v-radio :label="$i18n.t('contract manager')" value="contract manager"></v-radio>
                  </v-radio-group>
                  <label class="v-label theme--light" v-if="beneficiaries.role === 'customer'">
                    {{ $t("User role can create and comment tickets") }}
                  </label>
                  <label class="v-label theme--light" v-if="beneficiaries.role === 'contract manager'">{{
                    $t(
                      "The contract manager oversees the service contract for which he is  responsible." +
                        " It defines the indicators and ensures the contractual service levels"
                    )
                  }}</label>
                  <label class="v-label theme--light" v-if="beneficiaries.role === 'operational manager'">
                    {{ $t("Responsible operational in charge of the contract") }}
                  </label>
                </v-flex>
                <v-flex xs3 class="pt-4">
                  <strong class="required-label">{{ $t("Client") }} :</strong>
                </v-flex>
                <v-flex xs9>
                  <v-select
                    :items="clientsList"
                    item-value="_id"
                    item-text="name"
                    v-model="beneficiaries.client"
                    :rules="[() => !!beneficiaries.client || $i18n.t('Required field')]"
                  ></v-select>
                </v-flex>
                <v-flex xs3 class="pt-4 hidden-xs-only" v-if="beneficiaries.client">
                  <strong>{{ $t("Contracts") }} :</strong>
                </v-flex>
                <v-flex xs12 sm9 md9 lg9>
                  <v-card v-if="beneficiaries.client">
                    <v-list subheader two-line :disabled="!isAdmin">
                      <v-list-tile v-for="contract in filteredContractsByClient" :key="contract._id">
                        <v-flex xs2 sm2 md1 lg1>
                          <v-list-tile-action>
                            <v-checkbox :disabled="!isAdmin" v-model="contract.selected"> </v-checkbox>
                          </v-list-tile-action>
                        </v-flex>
                        <v-flex xs5 sm6 md5 lg6>
                          <v-list-tile-content>
                            <v-list-tile-title>{{ contract.name }}</v-list-tile-title>
                            <v-list-tile-sub-title>{{ contract.client }}</v-list-tile-sub-title>
                          </v-list-tile-content>
                        </v-flex>
                        <v-flex xs6 sm4 md8 lg6 px-0>
                          <v-list-tile-action>
                            <v-select
                              :disabled="!isAdmin"
                              dense
                              :items="contractRoles"
                              :label="$t('Role')"
                              v-model="contract.role"
                            >
                              <template slot="item" slot-scope="data">
                                <v-list-tile-content>
                                  <v-list-tile-title>
                                    {{ data.item.text }}
                                  </v-list-tile-title>
                                  <v-list-tile-sub-title>
                                    <template v-if="data.item.value === 'viewer'">
                                      {{ $t("Viewer role can only see tickets") }}
                                    </template>
                                    <template v-if="data.item.value === 'customer'">
                                      {{ $t("User role can create and comment tickets") }}
                                    </template>
                                  </v-list-tile-sub-title>
                                </v-list-tile-content>
                              </template>
                            </v-select>
                          </v-list-tile-action>
                        </v-flex>
                      </v-list-tile>
                    </v-list>
                  </v-card>
                </v-flex>
                <v-flex xs12 sm12 md12 lg12>
                  <div class="validate-btn" v-if="beneficiaries.client">
                    <v-btn :loading="inProgress" large color="primary" @click="validateFrom">
                      {{ $t("Create") }}
                    </v-btn>
                  </div>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-form>
        </v-card>
        <v-card class="elevation-1">
          <v-container fluid grid-list-md>
            <v-layout row wrap v-if="Object.keys(results).length > 0">
              <v-flex xs12 sm12 md6 lg6>
                <v-card>
                  <v-card-title
                    ><h4>{{ $t("Create") }}</h4></v-card-title
                  >
                  <v-divider></v-divider>
                  <v-list dense>
                    <v-list-tile v-bind:key="created.email" v-if="results.created" v-for="created in results.created">
                      <v-list-tile-title>{{ created.email }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ created._id }}</v-list-tile-sub-title>
                    </v-list-tile>
                  </v-list>
                </v-card>
              </v-flex>
              <v-flex xs12 sm12 md6 lg6>
                <v-card>
                  <v-card-title
                    ><h4>{{ $t("Errors") }}</h4></v-card-title
                  >
                  <v-divider></v-divider>
                  <v-list dense>
                    <v-list-tile v-bind:key="error.email" v-if="results.errors" v-for="error in results.errors">
                      <v-list-tile-title>{{ error.email }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ error.msg }}</v-list-tile-sub-title>
                    </v-list-tile>
                  </v-list>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import Vue from "vue";
import { USER_TYPE, USER_ROLES, BENEFICIARY_ROLE_LIST } from "@/constants.js";
import { mapGetters } from "vuex";

export default {
  metaInfo() {
    return {
      title: this.$i18n.t("New beneficiaries by contract")
    };
  },
  data() {
    return {
      search: null,
      items: [],
      selected: [],
      members: [],
      selectedUsers: [],
      beneficiaries: {
        users: [],
        role: "",
        client: "",
        contracts: []
      },
      results: {},
      organization: null,
      isLoading: false,
      headers: [
        {
          text: "Name",
          align: "left",
          value: "name",
          width: "20%"
        },
        { text: "Email", align: "left", value: "email", width: "30%" },
        { text: "Phone", align: "left", value: "phone", width: "20%" },
        { text: "Departement", align: "left", value: "departement", width: "20%" },
        { text: "Job Title", align: "left", value: "jobTitle", width: "20%" },
        { text: "Notify", align: "left", value: "notify", width: "10%" }
      ],
      valid: true,
      contractRoles: USER_ROLES.beneficiary.map(role => ({ text: this.$i18n.t(role), value: role })),
      inProgress: false
    };
  },
  created() {
    this.$store.dispatch("client/fetchClients");
  },
  computed: {
    ...mapGetters({
      contractList: "contract/getContractsMeta",
      clientsList: "client/getClients",
      currentUserId: "currentUser/getId"
    }),
    isAdmin() {
      return this.$auth.check(USER_TYPE.ADMIN);
    },
    filteredContractsByClient() {
      if (!this.beneficiaries.client) return [];
      const contracts = this.contractList
        .filter(contract => contract.clientId === this.beneficiaries.client)
        .map(contract => {
          Vue.set(contract, "contract_id", contract._id);
          Vue.set(contract, "selected", this.isContractUser(contract._id));
          Vue.set(contract, "role", this.getRoleByContractId(contract._id) || "viewer");
          return contract;
        });
      return contracts;
    }
  },
  methods: {
    getMembers() {
      if (this.organization === null) return;
      this.results = {};
      this.$http
        .searchMembersOf(this.organization)
        .then(results =>
          results.map(person => ({
            name: `${person.firstname} ${person.lastname}`,
            email: person.preferredEmail,
            phone: person.main_phone ? person.main_phone : "",
            jobTitle: person.job_title ? person.job_title : "",
            departement: person.departement || ""
          }))
        )
        .then(results => (this.members = results))
        .catch(console.log) // eslint-disable-line no-console
        .finally(() => (this.isLoading = false));
    },
    updateSelectedUsers() {
      let users = this.selected.map(user => ({
        type: "beneficiary",
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        jobTitle: "",
        notify: user.notify || false
      }));
      this.beneficiaries.users = users;
    },
    initRole() {
      this.beneficiaries.role = "";
      this.beneficiaries.contracts = [];
    },
    isContractUser(contract_id) {
      const userContracts =
        this.beneficiaries.contracts && this.beneficiaries.contracts.map(({ contract }) => contract);
      return userContracts.includes(contract_id);
    },
    getRoleByContractId(id) {
      const { role } = this.beneficiaries.contracts.find(contract => contract.contract === id) || { role: "viewer" };
      return role;
    },
    validateFrom() {
      if (this.$refs.form.validate()) {
        this.createBeneficiaries();
      } else {
        this.$store.dispatch("ui/displaySnackbar", {
          message: this.$i18n.t("the required fields must be filled"),
          color: "error"
        });
      }
    },
    createBeneficiaries() {
      if (this.beneficiaries.role === BENEFICIARY_ROLE_LIST.CONTRACT_MANAGER) {
        this.beneficiaries.contracts = [];
      } else {
        this.beneficiaries.contracts = this.filteredContractsByClient
          .filter(({ selected }) => selected === true)
          .map(({ contract_id, role }) => ({ contract_id, role }));
      }
      this.inProgress = true;
      this.$http
        .createBeneficiaries(this.beneficiaries)
        .then(response => {
          this.results = response;
          this.inProgress = false;
          this.resetForm();
        })
        .catch(error => {
          this.inProgress = false;
          this.$store.dispatch("ui/displaySnackbar", {
            message: this.$i18n.t(error.response.data.error.details),
            color: "error"
          });
        });
    },
    resetUserContracts(oldVal, newVal) {
      if (!!oldVal && !!newVal) {
        this.$set(this.beneficiaries, "contracts", []);
      }
    },
    resetForm() {
      this.$refs.form.reset();
      this.selected = [];
      this.members = [];
      this.selectedUsers = [];
    }
  },
  watch: {
    search(val) {
      if (!val || val === null) {
        return;
      }
      this.isLoading = true;
      this.$http
        .searchOrgs(val)
        .then(results => (this.items = results))
        .catch(console.log) // eslint-disable-line no-console
        .finally(() => (this.isLoading = false));
    },
    selected: "updateSelectedUsers",
    "beneficiaries.client": "resetUserContracts"
  }
};
</script>

<style scoped>
.v-toolbar__title {
  color: white;
}

.search-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.validate-btn {
  padding-top: 3rem;
  display: flex;
  flex-direction: row-reverse;
}
</style>
